import React, { useState, useRef, useEffect } from 'react';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { callApi } from '../../api/callApi';
import { showToastNotification } from '../../utility/helper';
import { IPreviousStatus } from '../../interfaces/order';
import useConfirmationModal from '../../hooks/useConfirmationModal';
import ConfirmationModal from '../UI/Modal/ConfirmationModal';

const statusStyles: any = {
    "PENDING": { label: "Pending", bgColor: "#f0ad4e" },
    "ACCEPTED": { label: "Accept", bgColor: "#5cb85c" },
    "PICKED UP": { label: "Picked Up", bgColor: "#5bc0de" },
    "ON THE WAY": { label: "On The Way", bgColor: "#428bca" },
    "DELIVERED": { label: "Delivered", bgColor: "#5cb85c" },
    "CANCELLED": { label: "Cancel", bgColor: "#d9534f" },
    "UNDER REFUND PROCESS": { label: "Under Refund Process", bgColor: "#777" },
    "CLIENT UPDATED ORDER": { label: "Client Update Order", bgColor: "#f0ad4e" },
    "REFUNDED": { label: "Refund", bgColor: "#5cb85c" },
    "REFUNDED REJECTED": { label: "Refund Rejected", bgColor: "#d9534f" }
};

// Statuses that should not be clickable in dropdown
const NON_CLICKABLE_STATUSES = ["CLIENT UPDATED ORDER", "REFUNDED", "REFUNDED REJECTED"];

export const updateOrderStatus = async (status: string, orderId: string, updateStatus: () => void) => {
    try {
        await callApi(API_URL_2 + API_ENDPOINT.order + `/orders/update-status`, {
            method: 'PUT',
            data: { id: orderId, status }
        });
        showToastNotification('success', 'Order Status updated successfully');
        updateStatus();
    } catch (error: any) {
        showToastNotification('danger', error?.message ?? 'Failed to update status');
        console.error('Failed to update status:' + error?.message);
    }
};

const StatusDropDown: React.FC<{
    CurrentStatus: string;
    orderId: string;
    orderStatus: string[];
    previousStatus: IPreviousStatus[];
    // driverDetail: IDriverInfo | string | null
    updateStatus: () => void;
}> = ({ CurrentStatus, orderId, updateStatus = () => { } }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();

    // const CONFIRMATION_STATUSES = ["PICKED UP", "ON THE WAY", "DELIVERED"];

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
                menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get allowed statuses based on current status
    const getAllowedStatuses = (): string[] => {
        switch (CurrentStatus) {
            case "PENDING":
                return ["ACCEPTED", "CANCELLED"];
            case "ACCEPTED":
                return ["PICKED UP", "CANCELLED"];
            case "PICKED UP":
                return ["ON THE WAY", "CANCELLED"];
            case "ON THE WAY":
                return ["DELIVERED", "CANCELLED"];
            case "DELIVERED":
                return ["UNDER REFUND PROCESS"];
            default:
                return [];
        }
    };

    const filteredOrderStatus = getAllowedStatuses().filter(status =>
        !NON_CLICKABLE_STATUSES.includes(status)
    );

    const currentStatusStyle = statusStyles[CurrentStatus] || { label: CurrentStatus, bgColor: "#6c757d" };

    const handleStatusUpdate = (status: string) => {
        // if (status === 'PICKED UP' && !driverDetail) {
        //     return showToastNotification('danger', 'Please assign driver first');
        // }
        if (status === 'CANCELLED') {
            confirmAction({
                title: 'Confirm Status Change',
                message: `Are you sure you want to change the order status to "${status}"?`,
                onConfirm: () => {
                    updateOrderStatus(status, orderId, updateStatus);
                    setIsOpen(false);
                },
            });
        } else {
            updateOrderStatus(status, orderId, updateStatus);
            setIsOpen(false);
        }
    };

    return (
        <>
            <div className="dropdown">
                <button
                    ref={buttonRef}
                    className="btn btn-secondary dropdown-toggle border py-1 px-2"
                    type="button"
                    onClick={toggleDropdown}
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    style={{ backgroundColor: currentStatusStyle.bgColor, color: '#fff' }}
                    aria-expanded={isOpen}
                >
                    {CurrentStatus}
                </button>
                <div
                    ref={menuRef}
                    className={`dropdown-menu ${isOpen ? 'show' : ''}`}
                >
                    {filteredOrderStatus.map((status, index) => {
                        const { label } = statusStyles[status] ?? { label: status };
                        return (
                            <a
                                className="dropdown-item"
                                href="#"
                                key={index}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleStatusUpdate(status);
                                }}
                            >
                                {label}
                            </a>
                        )
                    })}
                </div>
            </div>
            <ConfirmationModal {...modalProps} show={showModal} />
        </>
    );
};

export default StatusDropDown;